import { createSlice } from '@reduxjs/toolkit';

/**
 * Gateways — the neutral domain's state. Replaced the vendor-scoped ControlGateways
 * slice, which was named after Control even though TTLock gateways ran through it too.
 *
 * Everything here is gateway-shaped. Per-DEVICE live checks are not: they live in the
 * Devices slice (checkDeviceLive), keyed by deviceID.
 *
 * `controlReachable: false` is a DEGRADED state — last-known rows shown with a banner —
 * NOT an error. Only a genuine request failure sets `error`.
 */
const initialState = {
	// ── Org monitor: the per-gateway array from GET /gateways/monitor ──
	monitor: [],
	controlReachable: true,
	lastUpdated: null,
	errors: [],
	monitorLoading: false,
	monitorLoadingComplete: false,
	error: null,

	// ── The two on-demand checks, keyed by deviceID ──
	// status is the server's verdict: 'online' | 'offline' | 'unknown' | 'missing'.
	// Absent means never checked this session.
	statusByDeviceID: {}, // { [deviceID]: { loading, status, checkedAt } }
	firmwareByDeviceID: {}, // { [deviceID]: { loading, needUpgrade, version, checkedAt } }

	// ── Scoped reads from OUR OWN database (no vendor round trip) ──
	// One request answers both resolutions: `buckets` is the per-day roll-up the history
	// strip draws, `records` + `nextBefore` the cursor-paginated timeline the feed lists.
	// `loading` is 'replace' | 'append' | false — the feed dims on one and spins on the other.
	// The Gateways tab does NOT read this: the org monitor already carries every gateway's
	// history in a single query. This fills the deep-link case.
	historyByDeviceID: {}, // { [deviceID]: { loading, buckets, records, nextBefore } }
	// The org-wide read is in flight. Per-device entries carry their own `loading`; this is
	// the one that covers "every gateway at once", so a row cannot spin on its own for it.
	orgHistoryLoading: false,
	// Last-known bound devices, from the gateway↔lock binding on lock heartbeats.
	lastKnownLocksByDeviceID: {}, // { [deviceID]: { checkedAt, locks } }
	lastKnownLocksLoadingByDeviceID: {},

	// ── "Sync to inventory" — import an unmanaged gateway, keyed by the vendor's gatewayId ──
	syncByGatewayId: {},

	// ── Org setting 161 (gateway monitoring on/off) ──
	// Starts loading:true so a one-shot read waits for the REAL value instead of acting on
	// the placeholder and deciding an already-enabled org is off.
	connectivityConfig: { enabled: false },
	connectivityConfigLoading: true
};

const at = (map, key) => map[key] || {};

const gatewaysSlice = createSlice({
	name: 'gateways',
	initialState,
	reducers: {
		// ── Org monitor ─────────────────────────────────────────────────────────
		loadMonitor: (state) => {
			state.monitorLoading = true;
			state.error = null;
		},
		loadMonitorSuccess: (state, action) => {
			const { gateways = [], controlReachable = true, lastUpdated = null, errors = [] } = action.payload || {};
			state.monitor = gateways;
			state.controlReachable = controlReachable;
			state.lastUpdated = lastUpdated;
			state.errors = errors;
			state.monitorLoading = false;
			state.monitorLoadingComplete = true;
		},
		loadMonitorError: (state, action) => {
			state.monitorLoading = false;
			// Mark complete so the UI shows an error state instead of a blank table stuck
			// behind a first load that never resolves.
			state.monitorLoadingComplete = true;
			state.error = action.payload;
		},

		/**
		 * Merge a fresh check snapshot into the matching monitor row so the panel updates
		 * in place — no org-wide re-fetch. Locks are replaced only when the check produced
		 * them (an online gateway); an offline result keeps the last-known list rather than
		 * blanking it.
		 */
		applyGatewaySnapshot: (state, action) => {
			const gw = action.payload?.gateway;
			if (!gw || gw.deviceID == null) return;
			let row = (state.monitor || []).find((g) => g.deviceID === gw.deviceID);
			if (!row) {
				// No monitor row yet (deep link — the org-wide monitor was never fetched).
				// UPSERT so a check alone fully populates the scoped panel.
				row = { deviceID: gw.deviceID, gatewayId: gw.gatewayId ?? null, name: gw.name, locks: [] };
				if (!Array.isArray(state.monitor)) state.monitor = [];
				state.monitor.push(row);
			}
			row.status = gw.status;
			row.online = gw.status === 'online';
			row.lastHeartbeatAt = gw.checkedAt;
			if (gw.status === 'online') row.lastConfirmedAt = gw.checkedAt;
			if (Array.isArray(gw.locks) && gw.locks.length > 0) {
				row.locks = gw.locks.map((l) => {
					// lockId is a string in the snapshot and numeric in monitor rows — match
					// loosely, keep the row's extra fields (mac…), and only let a REAL battery
					// reading overwrite the last-known one: an unreachable probe returns null,
					// which must not blank the display.
					const prev = (row.locks || []).find((p) => String(p.lockId) === String(l.lockId)) || {};
					return {
						...prev,
						...l,
						lockId: prev.lockId ?? l.lockId,
						battery: l.battery ?? prev.battery ?? null,
						// The check's tri-state reachability IS the freshest synced status. Without
						// it an upserted row has none, and every device dot falls back to "Unknown".
						lastSyncedStatus: l.reachable ?? prev.lastSyncedStatus ?? null
					};
				});
			}
		},

		// ── Status check ────────────────────────────────────────────────────────
		checkStatus: (state, action) => {
			const { deviceID } = action.payload;
			state.statusByDeviceID[deviceID] = { ...at(state.statusByDeviceID, deviceID), loading: true };
		},
		checkStatusSuccess: (state, action) => {
			const { deviceID, gateway, throttled, checkedAt } = action.payload;
			state.statusByDeviceID[deviceID] = {
				loading: false,
				// A throttled reply carries no fresh verdict — keep the one we had.
				status: throttled ? at(state.statusByDeviceID, deviceID).status : gateway?.status,
				checkedAt: throttled ? checkedAt : gateway?.checkedAt
			};
		},
		checkStatusError: (state, action) => {
			const { deviceID } = action.payload;
			state.statusByDeviceID[deviceID] = { ...at(state.statusByDeviceID, deviceID), loading: false };
		},

		// ── Firmware check ──────────────────────────────────────────────────────
		checkFirmware: (state, action) => {
			const { deviceID } = action.payload;
			state.firmwareByDeviceID[deviceID] = { ...at(state.firmwareByDeviceID, deviceID), loading: true };
		},
		checkFirmwareSuccess: (state, action) => {
			const { deviceID, firmware } = action.payload;
			state.firmwareByDeviceID[deviceID] = { loading: false, ...firmware };
		},
		checkFirmwareError: (state, action) => {
			const { deviceID } = action.payload;
			state.firmwareByDeviceID[deviceID] = { ...at(state.firmwareByDeviceID, deviceID), loading: false };
		},

		// ── History: both resolutions, one request ──────────────────────────────
		// `before` (a HeartbeatID cursor from the previous page's nextBefore) asks for the
		// next OLDER page of records and appends it; without it the read replaces.
		//
		// 🛑 `recordsRequested` is the "we have asked" mark, and it is what the drawer and the
		// activity feed guard on — NOT `records`, and NOT `loading`. Both of those are answers,
		// and every answer eventually clears: a FAILED read leaves records undefined and loading
		// false, which reads exactly like "never asked", so a guard on those two re-dispatches
		// on its own error, forever. Once set it is never cleared, so a failure stays quiet
		// until something asks again on purpose (Check now, or a fresh session).
		loadHistory: (state, action) => {
			const { deviceID, before, limit } = action.payload;
			const held = at(state.historyByDeviceID, deviceID);
			state.historyByDeviceID[deviceID] = {
				...held,
				recordsRequested: true,
				// Remembered so the REFRESH a "Check now" dispatches re-reads the same window the
				// feed asked for. Without it the refresh falls back to the saga's default and the
				// drawer's 30-row feed silently becomes a 20-row one after every check.
				limit: before ? held.limit : (limit ?? held.limit),
				// A retry must clear the previous failure, or the feed keeps apologising while
				// it is busy succeeding.
				error: null,
				loading: before ? 'append' : 'replace'
			};
		},
		loadHistorySuccess: (state, action) => {
			const { deviceID, buckets, records = [], nextBefore = null, append } = action.payload;
			const held = at(state.historyByDeviceID, deviceID);
			state.historyByDeviceID[deviceID] = {
				loading: false,
				recordsRequested: true,
				error: null,
				// Carried through: this reducer REBUILDS the entry, so the window the feed asked
				// for has to survive the answer for the next refresh to re-read it.
				limit: held.limit,
				// An append page carries the same buckets; don't let a paginated read
				// re-write the strip with a narrower window than the one on screen.
				buckets: append ? held.buckets : buckets || [],
				records: [...(append ? held.records || [] : []), ...records],
				nextBefore
			};
		},
		loadHistoryError: (state, action) => {
			const { deviceID, error } = action.payload;
			// The reason is kept, not just the fact: a feed that says "no checks yet" when the
			// request actually failed is telling the user the opposite of what happened.
			state.historyByDeviceID[deviceID] = {
				...at(state.historyByDeviceID, deviceID),
				loading: false,
				error: error || true
			};
		},

		// ── History for EVERY gateway, one read ─────────────────────────────────
		loadOrgHistory: (state) => {
			state.orgHistoryLoading = true;
		},
		loadOrgHistorySuccess: (state, action) => {
			const { buckets } = action.payload;
			// MERGE, never replace. This read answers buckets only, so a device whose records
			// were already paged in must keep them — and `records` must stay UNDEFINED where it
			// has not been read, because that absence is what tells the activity feed it still
			// has to ask. Writing [] here would make an unread gateway look like an empty one.
			// It also must NOT touch `loading`: that flag belongs to the per-device read, and
			// clearing it under one that is still in flight makes the drawer see "idle, nothing
			// read" and fire a duplicate.
			Object.entries(buckets || {}).forEach(([deviceID, days]) => {
				state.historyByDeviceID[deviceID] = { ...at(state.historyByDeviceID, deviceID), buckets: days };
			});
			state.orgHistoryLoading = false;
		},
		loadOrgHistoryError: (state) => {
			state.orgHistoryLoading = false;
		},

		// ── Last-known bound devices ────────────────────────────────────────────
		loadGatewayLocks: (state, action) => {
			state.lastKnownLocksLoadingByDeviceID[action.payload.deviceID] = true;
		},
		loadGatewayLocksSuccess: (state, action) => {
			const { deviceID, checkedAt, locks } = action.payload;
			state.lastKnownLocksByDeviceID[deviceID] = { checkedAt, locks: locks || [] };
			state.lastKnownLocksLoadingByDeviceID[deviceID] = false;
		},
		loadGatewayLocksError: (state, action) => {
			state.lastKnownLocksLoadingByDeviceID[action.payload.deviceID] = false;
		},

		// ── Sync one unmanaged vendor gateway into inventory ────────────────────
		syncGateway: (state, action) => {
			state.syncByGatewayId[action.payload.gatewayId] = { loading: true };
		},
		syncGatewaySuccess: (state, action) => {
			state.syncByGatewayId[action.payload.gatewayId] = { loading: false };
		},
		syncGatewayError: (state, action) => {
			state.syncByGatewayId[action.payload.gatewayId] = { loading: false };
		},

		// ── Org monitoring setting ──────────────────────────────────────────────
		loadConnectivityConfig: (state) => {
			state.connectivityConfigLoading = true;
		},
		loadConnectivityConfigSuccess: (state, action) => {
			state.connectivityConfig = { ...state.connectivityConfig, ...action.payload };
			state.connectivityConfigLoading = false;
		},
		loadConnectivityConfigError: (state) => {
			state.connectivityConfigLoading = false;
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
	}
});

export const gatewaysActions = gatewaysSlice.actions;
export default gatewaysSlice.reducer;
